import { Injectable, inject } from '@angular/core';
import {isDefined, runtimeEventManager} from '@wm/core';
import { PageWsService } from '@wms/app/workspace';
import { BOOLEAN_ATTRS, hyphenate, MarkupManagerService } from '@wms/core';
import {filter, find, forEach, get, includes, isEmpty, isObject, isUndefined, keys, map, startsWith} from 'lodash-es';
import {EventManagerService} from "@wms/app";

@Injectable({
  providedIn: 'root'
})
export class LiveWidgetMarkupManagerService {
  gridColumnProps = ['type', 'backgroundColor', 'textColor', 'sortby','generator', 'readonly', 'datepattern', 'formatpattern', 'custompipeformat', 'currencypattern', 'fractionsize', 'suffix', 'prefix', 'accessroles', 'pcDisplay', 'mobileDisplay', 'tabletDisplay', 'validationmessage',
    'dataset', 'datafield', 'placeholder', 'displayfield', 'displaylabel', 'searchkey', 'matchmode', 'groupby', 'match', 'collapsible', 'showcount', 'dateformat', 'debouncetime', 'defaultvalue', 'sortable', 'caseinsensitive', 'searchable', 'show', 'rowactionsposition', 'limit', 'filterwidget', 'filterplaceholder', 'minvalue', 'maxvalue', 'maxchars', 'regexp',
    'checkedvalue', 'uncheckedvalue', 'showdropdownon', 'dataentrymode', 'autoclose', 'filterdataset', 'filterdatafield', 'filterdisplayfield', 'filterdisplaylabel', 'filtersearchkey', 'filteronfilter', 'editdatepattern', 'exportexpression', 'filterexpressions', 'period','outputformat',
    'editinputtype', 'filterinputtype', 'step', 'showineditmode'];
  pageWsService: PageWsService;
  eventManager: EventManagerService;

  constructor(private markupManagerService: MarkupManagerService) {
    runtimeEventManager.setService('LiveWidgetMarkupManagerService', this);
    this.pageWsService = inject(PageWsService);
    this.eventManager = inject(EventManagerService);
  }

  /**
 * Sets the column definitions to the markup attributes
 */
  setAttrMarkup(columnDef: any, widgetType: any) {
    let widgetId,
      widgetMarkup = '<' + widgetType + ' ';

    if (widgetType === 'wm-form-field' || widgetType === 'wm-filter-field') { // generate the widgetId for the form-field
      widgetId = !columnDef['widgetid'] ? this.markupManagerService.getUniqueIdByType(widgetType) : columnDef['widgetid'];
      /*Assign the column key as widget name*/
      widgetMarkup += 'widgetid=' + widgetId + ' widget-id=' + widgetId + ' widgettype=' + widgetType + ' name=' + (columnDef.key || columnDef.field) + ' ';
    }

    widgetMarkup += '></' + widgetType + '>';

    let widgetMarkupElement: any = $(widgetMarkup);

    forEach(columnDef, (propertyValue, propertyName) => {
      if (!isDefined(propertyValue) || (propertyName === 'widget' && propertyValue.widgetType)) {
        return;
      }
      if (propertyName === 'displaywidget') {
        let widget = propertyValue.widgetType ? propertyValue.widgetType.replace('wm-', '') : propertyValue;
        widgetMarkupElement?.attr('widget', widget);
        if(propertyValue.widgetType)
          widgetMarkupElement?.attr('widgetname', propertyValue.defaults.widgetName);
      } else if (BOOLEAN_ATTRS.has(propertyName)) {
        widgetMarkupElement[0].setAttribute(propertyName, propertyValue);
      } else if (propertyName.indexOf('$') !== 0) {
        widgetMarkupElement?.attr(hyphenate(propertyName), propertyValue);
      }
    });

    return widgetMarkupElement[0].outerHTML;
  }

  //Get the markup for grid columns
  getColumnMarkup(columnDef: any) {
    var $el = $('<wm-table-column></wm-table-column>'),
      events = filter(keys(columnDef), function (key) { return startsWith(key, 'on'); }),
      eventValue,
      columnDefProps: any;

    $el.append(columnDef.customExpression || '');

    columnDefProps = {
      'binding': columnDef.field,
      'caption': columnDef.displayName || '',
      'widget-type': columnDef.widgetType,
      'width': (columnDef.width && columnDef.width !== '*') ? columnDef.width : undefined,
      'textAlignment': (columnDef.textAlignment && columnDef.textAlignment !== 'left') ? columnDef.textAlignment : undefined,
      'primary-key': columnDef.primaryKey,
      'col-class': columnDef.class,
      'col-ng-class': columnDef.ngclass,
      'edit-widget-type': columnDef.editWidgetType,
      'related-entity-name': columnDef.relatedEntityName,
      'filter-on': columnDef.filterOn,
      'searchable': columnDef.searchable,
      'sortby':columnDef.sortby,
      'show':columnDef.show,
      'pcdisplay': columnDef.pcDisplay,
      'tabletdisplay': columnDef.tabletDisplay,
      'mobiledisplay': columnDef.mobileDisplay
    };

    forEach(this.gridColumnProps, function (key) {
      if(columnDef[key]) columnDefProps[key] = columnDef[key];
    });

    $el.attr(columnDefProps);
    $el[0].setAttribute('readonly', columnDef.readonly);
    if (columnDef.disabled) {
      $el[0].setAttribute('disabled', columnDef.disabled);
    }
    if (columnDef.required) {
      $el[0].setAttribute('required', columnDef.required);
    }
    // Adding this property to markup only if showineditmode is false or bound to some condition
    if (isDefined(columnDef.showineditmode) && columnDef.showineditmode != true) {
      $el[0].setAttribute('showineditmode', columnDef.showineditmode);
    }
    forEach(events, (event) => {
      eventValue = columnDef[event];
      if (eventValue) {
        $el[0].setAttribute(hyphenate(event), eventValue);
      }
    });
    return $el;
  }

  //Set the column markup based on header config
  setColumnMarkup(columnDefs: any, headerConfig: any, $wrapper: any) {
    //If header config is not preset, loop over columndefs to create the markup
    if (!headerConfig) {
      forEach(columnDefs, (columnDef) => {
        $wrapper.append(this.getColumnMarkup(columnDef));
      });
      return;
    }
    forEach(headerConfig, (config) => {
      var $el,
        columnDef,
        $groupEl;
      //For group, create the group markup
      if (config.columns && config.columns.length) {
        $groupEl = $('<wm-table-column-group></wm-table-column-group>');
        $groupEl.attr({
          'name': config.field,
          'caption': config.displayName,
          'accessroles': config.accessroles,
          'textalignment': (config.textAlignment && config.textAlignment !== 'center') ? config.textAlignment : undefined,
          'backgroundcolor': config.backgroundColor || undefined,
          'col-class': config.class || undefined
        });
        $wrapper.append($groupEl);
        //Generare the markup for inner columns
        this.setColumnMarkup(columnDefs, config.columns, $groupEl);
      } else {
        columnDef = find(columnDefs, { 'field': config.field });
        if (columnDef) {
          $el = this.getColumnMarkup(columnDef);
          $wrapper.append($el);
        }
      }
    });
  }

  /**
 * get the markup object for the respective grid
 */
  getGridDefsMarkup(columnDefs: any, buttonDefs: any, rowDefs: any, rowActionsDefs: any, headerConfig: any) {
    /*get the markup object for the respective grid*/
    var actionsMarkup = '',
      rowActionsMarkup = '',
      rowDefsMarkup = '',
      rowPartialMarkup = '',
      isColumnDefsDefined = isDefined(columnDefs),
      isButtonDefsDefined = isDefined(buttonDefs),
      isRowActionDefsDefined = isDefined(rowActionsDefs),
      isRowDefsDefined = isDefined(rowDefs),
      isPartialParamsDefined = isRowDefsDefined && isDefined(rowDefs.partialParams),
      rowDefMarkup,
      buttonDefMarkup,
      partailParamMarkup,
      $wrapper = $('<div></div>');
    if (isColumnDefsDefined) {
      /*loop over the column defs and prepare new grid-column nodes*/
      this.setColumnMarkup(columnDefs, headerConfig, $wrapper);
    }
    if (isButtonDefsDefined) {
      /*loop over the button defs and prepare new grid-action nodes*/
      forEach(buttonDefs, (buttonDef) => {
        buttonDefMarkup = this.setAttrMarkup(buttonDef, 'wm-table-action');
        actionsMarkup += buttonDefMarkup;
      });
    }
    if (isRowActionDefsDefined) {
      /*loop over the button defs and prepare new grid-action nodes*/
      forEach(rowActionsDefs, (rowActionDef) => {
        rowDefMarkup = this.setAttrMarkup(rowActionDef, 'wm-table-row-action');
        rowActionsMarkup += rowDefMarkup;
      });
    }
    if (isRowDefsDefined && !isEmpty(rowDefs)) {
      var newRowDefs = Object.assign({}, rowDefs);
      delete newRowDefs['partialParams'];
      rowDefsMarkup = this.setAttrMarkup(newRowDefs, 'wm-table-row');
    }
    if (isPartialParamsDefined) {
      /*loop over the partial params and prepare new param nodes*/
      forEach(rowDefs.partialParams, (param) => {
        if (param.value) {
          partailParamMarkup = this.setAttrMarkup(param, 'wm-param');
          rowPartialMarkup += partailParamMarkup;
        }
      });
      rowDefsMarkup = $(rowDefsMarkup).append(rowPartialMarkup)[0].outerHTML
    }
    return {
      columns: $wrapper.html(),
      actions: actionsMarkup,
      rowActions: rowActionsMarkup,
      rowDefs: rowDefsMarkup
    };
  }

  /**
 * @ngdoc function
 * @name wmCore.$LiveWidgetsMarkupManager#updateMarkupForGrid
 * @methodOf wmCore.$LiveWidgetsMarkupManager
 * @function
 *
 * @description
 * Update the grid markup when the datasource is bound to a variable
 *
 * @param {options} options containing the form field and button properties
 */
  updateMarkupForGrid(options: any) {

    /*get the markup object for the respective grid*/
    let markup = this.getGridDefsMarkup(options.fieldDefs, options.buttonDefs, options.rowDefs, options.rowActionDefs, options.headerConfig),
      gridNode = this.markupManagerService.getMarkup()?.find('[name="' + options.widgetName + '"][widgetid="' + options.widgetId + '"]'),
      actionsNodes = gridNode?.find('wm-table-action'),
      rowActionsNodes = gridNode?.find('wm-table-row-action'),
      columnNodes = gridNode?.find('wm-table-column'),
      groupNodes = gridNode?.find('wm-table-column-group'),
      rowNodes = gridNode?.find('wm-table-row'),
      columnsMarkup = markup.columns,
      actionsMarkup = markup.actions,
      rowActionsMarkup = markup.rowActions,
      rowMarkup = markup.rowDefs;
    this.setWidgetProperties(gridNode, options.properties);
    if (isDefined(columnsMarkup)) {
      columnNodes?.remove();
      groupNodes?.remove();
      gridNode?.append(columnsMarkup);
      // $rs.$emit('wms:compile-grid-columns', options.scopeId, columnsMarkup); // TODO: check how to emit to root
    }
    actionsNodes?.remove();
    gridNode?.append(actionsMarkup);
    // $rs.$emit('wms:compile-grid-actions', options.scopeId, actionsMarkup);
    rowNodes?.remove();
    gridNode?.append(rowMarkup);
    // $rs.$emit('wms:compile-grid-rowdefs', options.scopeId, rowMarkup);
    rowActionsNodes?.remove();
    gridNode?.append(rowActionsMarkup);
    runtimeEventManager.emit('updateTableMarkup' + options.widgetId, gridNode);
    // $rs.$emit('wms:compile-grid-row-actions', options.scopeId, rowActionsMarkup, options.fromDesigner);

    /* save the markup changes in workspace */
    return this.saveProject(options);
  }

  /*Function to set the properties on the live widgets*/
  setWidgetProperties(targetNode: any, properties: any) {
    forEach(properties, (property) => {
      if (isUndefined(property.value)) {
        targetNode.removeAttr(property.key);
      } else {
        targetNode.attr(property.key, property.value);
      }
    });
  }
  //TODO: check how to save project
  /**Save the project after saving advanced settings and add actions to script file**/
  saveProject(options: any) {
    /*Timeout is required to wait till the markup is updated, then make a save call*/

    let promise;
    setTimeout(() => {
      // todo: this property has to be set only when columns values have changed.
      this.pageWsService.setWidgetPropertyChangedFlg(true);
      let customActions = options.customActions || [];
      if (customActions.length) {
        runtimeEventManager.emit('add-event-handler',
          {event:'liveWidget-action',
            options:{ 'widgetName' : options.widgetName,
              'eventPrefix': customActions,
              'args'       : options.args,
              'openScript' : options.openScript,
              // 'onSuccess'  : deferred.resolve
            }})
      } else if (options.openScript) {
        return this.pageWsService.saveActiveTabContent().then(() => {
          runtimeEventManager.emit('navigateToScript', options.eventName);
        });
      }
      return this.pageWsService.saveActiveTabContent();
    })
    // var deferred = $q.defer();

    // setTimeout( () => {
    //     StudioActions.saveProject(options.fromDesigner)
    //         .then(function () {
    //             var customActions = options.customActions || [];
    //             if (customActions.length) {
    //                 /*Call the event handler function to add custom actions to the script.js file*/
    //                 $rs.$emit(
    //                     'add-event-handler',
    //                     'liveWidget-action',
    //                     {
    //                         'widgetName' : options.widgetName,
    //                         'eventPrefix': customActions,
    //                         'args'       : options.args,
    //                         'openScript' : options.openScript,
    //                         'onSuccess'  : deferred.resolve
    //                     }
    //                 );
    //             } else {
    //                 deferred.resolve();
    //             }

    //         });
    // });

    // return deferred.promise;
  }

  //Function to wrap the form field inside a grid column
  getFieldWithColumnWrapper(field: any, colWidth: any) {
    var colId = this.markupManagerService.getUniqueIdByType('gridcolumn'),
      columnWrapper;
    columnWrapper = $('<wm-gridcolumn name=' + colId + ' widgetid=' + colId + ' widget-id=' + colId + ' widgettype=' + "wm-gridcolumn" + ' columnwidth="' + colWidth + '"></wm-gridcolumn>');
    return columnWrapper.append(field);
  }

  //Function to check if new column can be inserted in the last row or a new row should be created
  isColumnInsertable(gridLastRow: any, itemsPerRow: any, colWidth: any) {
    var $gridCols,
      totalColWidth = 0;
    if (!gridLastRow.length) {
      return false;
    }

    $gridCols = gridLastRow.find('> wm-gridcolumn');
    //If number of columns present are more than itemsperrow, insert new row
    if ($gridCols.length > itemsPerRow) {
      return false;
    }

    $gridCols.each((index: any, elementNode: any) => {
      totalColWidth += + elementNode?.getAttribute('columnwidth');
    });
    //Insert new row, if present columns width is more than or equal to 12
    return totalColWidth + colWidth <= 12;
  }

  //Function to append the form field to a grid row inside layout grid
  appendField(field: any, grid: any, itemsPerRow: any) {
    var gridLastRow,
      rowId,
      rowWrapper,
      colWidth;
    itemsPerRow = Number(grid.attr('columns')) || itemsPerRow;
    colWidth = 12 / itemsPerRow;
    gridLastRow = grid.find('> wm-gridrow:last');
    //If grid last row as empty columns, add form field to this row. Otherwise, create a new row and append.
    if (this.isColumnInsertable(gridLastRow, itemsPerRow, colWidth)) {
      gridLastRow.append(this.getFieldWithColumnWrapper(field, colWidth));
    } else {
      rowId = this.markupManagerService.getUniqueIdByType('gridrow');
      rowWrapper = $('<wm-gridrow name=' + rowId + ' widgetid=' + rowId + ' widget-id=' + rowId + ' widgettype=' + "wm-gridrow" + '></wm-gridrow>');
      rowWrapper.append(this.getFieldWithColumnWrapper(field, colWidth));
      grid.append(rowWrapper);
    }
  }

  /*
 * Prepares the form-field markup inside a grid layout out of the fieldDefs provided
 * The function is called whenever there is a modification requested in fieldDefs from the UI, the cases are:
 * first time binding of dataset property, layout change for the widget, change in fieldDefs from designer
 */
  prepareFormFilterFieldsMarkup(options: any) {
    /*get the markup object for the respective grid*/
    var targetName = options.widgetName,
      columnDefs = options.fieldDefs,
      buttonDefs = options.buttonDefs,
      fromDesigner = options.fromDesigner,
      itemsPerRow = options.numColumns,
      bindDataSetChanged = options.bindDataSetChanged,
      targetNode = targetName ? this.markupManagerService.getMarkup()?.find('[name="' + targetName + '"][widgetid="' + options.widgetId + '"]') : options.targetNode,
      formFieldElement = options.formFieldElement,
      formActionElement = options.formActionElement,
      widgetType = options.widgettype,
      grid: any,
      hasLayoutGrid,
      i,
      newFFs,
      oldFFs,
      lastNodeParent;
    this.setWidgetProperties(targetNode, options.properties);
    grid = targetNode.find('> wm-layoutgrid').first();
    hasLayoutGrid = grid.length > 0;

    //Filter function to exclude the fields inside the inner live form
    let excludeInnerFields = (field: HTMLElement) => {
      return $(field).closest(widgetType).is(targetNode);
    }

    if (fromDesigner) { /* Do not change the layout. Re arrange the contents */

      // sort the column defs based on the `show` property
      newFFs =
        map(columnDefs, (colDef) => {
          // construct the elements
          forEach(colDef.fieldDefConfig, (val: any, key: string) => {
            if (key === 'widgetid') {
              return;
            }
            if (includes(['mobileDisplay', 'pcDisplay', 'tabletDisplay'], key)) {
              if ((isDefined(colDef[key]) && colDef[key].toString() === 'false') || colDef.fieldDefConfig[key] !== colDef[key]) {
                colDef.fieldDefConfig[key] = colDef[key];
              }
            } else {
              if (key === 'filterexpressions' && isObject(colDef[key])) {
                colDef[key] = JSON.stringify(colDef[key]);
              }
              if (key === 'widget') {
                const isWidgetProxy = get(colDef[key], 'widgetId');
                if (isWidgetProxy) {
                  colDef.fieldDefConfig[key] = colDef['displaywidget'] || 'text';
                }
              } else {
                colDef.fieldDefConfig[key] = colDef[key];
              }
              let bindKey = 'bind' + key;
              // setting the binding values on formField.
              if (colDef[bindKey]) {
                colDef.fieldDefConfig[key] = colDef[bindKey];
              }
            }
          });
          return $(this.setAttrMarkup(colDef.fieldDefConfig || colDef, options.formFieldElement))[0];
        });

      // get the existings form fields
      oldFFs = this.getExistingFormFields(targetNode, widgetType, formFieldElement);

      if (!hasLayoutGrid && !oldFFs.length) {
        this.generateFormMarkupInGridLayout({
          'targetNode': targetNode,
          'fieldDefs': columnDefs,
          'buttonDefs': buttonDefs,
          'formFieldElement': formFieldElement,
          'formActionElement': formActionElement
        });
      } else {

        // new fields added
        if (newFFs.length > oldFFs.length) {

          for (i = 0; i < oldFFs.length; i++) {
            oldFFs[i].replaceWith(newFFs[i]);
          }

          // add the new fields at the end of the last field.
          // if there are no existing form fields add the nodes to the live-form.
          if (!i) {
            lastNodeParent = targetNode;
          } else {
            lastNodeParent = $(newFFs[i - 1]).closest(widgetType);
          }
          for (i; i < newFFs.length; i++) {
            lastNodeParent.append(newFFs[i]);
          }
        } else if (newFFs.length < oldFFs.length) {
          // fields removed
          // remove the extra fields from the end

          for (i = 0; i < newFFs.length; i++) {
            oldFFs[i].replaceWith(newFFs[i]);
          }

          for (i; i < oldFFs.length; i++) {
            oldFFs[i].remove();
          }

        } else {
          // form field defs might have been modified
          // replace the old ones with the new

          for (i = 0; i < newFFs.length; i++) {
            oldFFs[i].replaceWith(newFFs[i]);
          }
        }

        // update the position of hidden form fields.

        /*Cleanup the grid columns which are empty and remove show property*/
        targetNode.find('> wm-layoutgrid wm-gridcolumn:empty').remove();
        targetNode.find('> wm-layoutgrid wm-gridrow:empty').remove();
        targetNode.find('> wm-layoutgrid wm-gridcolumn').removeAttr('show');

        //Get the form fields having show property true and outside the layoutgrid. Add these fields to layoutgrid.
        if (hasLayoutGrid) {
          filter(targetNode.find('>' + formFieldElement + '[show="true"]'), (field) => {
            let exclude = excludeInnerFields(field);
            if (exclude) {
              this.appendField(field, grid, itemsPerRow);
            }
          });
        }

        filter(targetNode.find(formActionElement), (field) => {
        let exclude = excludeInnerFields(field);
          if (exclude) {
            field.remove()
          }
        });

        if (buttonDefs) {
          for (i = 0; i <= buttonDefs.length - 1; i++) {
            targetNode.append(this.setAttrMarkup(buttonDefs[i], formActionElement));
          }
        }
      }
    } else {
      // if the form doesn't have a layoutgrid/ bindDataSetChanged is true generate the layout grid using columnDefs.
      if (!hasLayoutGrid || bindDataSetChanged) {
        this.generateFormMarkupInGridLayout({
          'targetNode': targetNode,
          'fieldDefs': columnDefs,
          'buttonDefs': buttonDefs,
          'itemsPerRow': itemsPerRow,
          'formFieldElement': formFieldElement,
          'formActionElement': formActionElement
        });
      } else {
        // layout property is changed... re-arrange the contents of layout grid accordingly.
        this.updateGridLayout(grid, options, itemsPerRow, formFieldElement);
      }
    }

    return targetNode.html();
  }

  // returns the form-fields of a live-form
  getExistingFormFields(targetNode: any, widgetType: any, formFieldElement: any) {
    return filter(targetNode.find(formFieldElement), (fieldEl) => {
      return $(fieldEl).closest('wm-liveform, wm-form, wm-livefilter').is(targetNode);
    });
  }

  /*
 * This method generates the layout grid along with form-fields.
 * This method is used for widgets like liveform and livefilter.
 * This method will be called when the widget's dataset is changed or when the widget doesn't contain a livegrid.
 */
  generateFormMarkupInGridLayout(options: any) {
    var formNode = options.targetNode,
      noOfColumns = options.itemsPerRow || 1,
      colDefs = options.fieldDefs,
      btnDefs = options.buttonDefs,
      formFieldElement = options.formFieldElement,
      formActionElement = options.formActionElement,
      noOfRows = Math.ceil(colDefs.length / noOfColumns), // number of grid rows to be created.
      colWidth = 12 / noOfColumns,
      count = 0,
      row,
      col,
      colDef,
      htm: any,
      gridId,
      rowId,
      colId,
      colDisplay;

    formNode.empty(); // remove all the elements from form.
    colDefs = colDefs || []; // when colDefs is undefined change it to an empty array

    gridId = this.markupManagerService.getUniqueIdByType('layoutgrid');
    htm = '<wm-layoutgrid name=' + gridId + ' widgetid=' + gridId + ' widgettype=' + "wm-layoutgrid" + '>';

    // based on the noOfRows and noOfColumns per rows generate the grid.
    for (row = 0; row < noOfRows; row++) {
      rowId = this.markupManagerService.getUniqueIdByType('gridrow');
      if (count < colDefs.length) {
        htm += '<wm-gridrow name=' + rowId + ' widgetid=' + rowId + ' widgettype=' + "wm-gridrow" + '>';
      }
      for (col = 0; col < noOfColumns && (count < colDefs.length); col++) {
        colDef = colDefs[count];
        // If multiple rows and column should be hidden merge column within same row
        if (colDef.show.toString() === 'false') {
          colDisplay = 'hide';
          col--;
        } else {
          colDisplay = 'show';
        }
        colId = this.markupManagerService.getUniqueIdByType('gridcolumn');
        htm += '<wm-gridcolumn show=' + colDisplay + ' name=' + colId + ' widgetid=' + colId + ' widgettype=' + "wm-gridcolumn" + ' columnwidth="' + colWidth + '">';
        htm += colDef ? this.setAttrMarkup(colDef, formFieldElement) : '';
        htm += '</wm-gridcolumn>';
        count++;
      }

      htm += '</wm-gridrow>';
    }
    htm += '</wm-layoutgrid>';

    //generate buttons markup
    forEach(btnDefs, (btnDef) => {
      htm += this.setAttrMarkup(btnDef, formActionElement);
    });

    // update the formNode with the generated markup.
    formNode.append(htm);
  }

  /*
 * Updates the grid layout, based on the column layout provided
 * This method will be called when the layout of the target widget is changed from property panel
 */
  updateGridLayout(grid: any, options: any, noOfColumns: any, formFieldElement: any) {
    var existingRows = grid.find(' > wm-gridrow'),
      existingColumns = existingRows.find(' > wm-gridcolumn'),
      colDefs = options.fieldDefs,
      existingNoOfFields = existingColumns.length,
      rowCount = Math.ceil(existingNoOfFields / noOfColumns), // no of rows to be created
      htm = '',
      count = 0,
      columnWidth = 12 / noOfColumns,
      colDefCount = 0,
      rowId,
      row,
      col,
      existingCol;

    grid.empty(); // remove the elements from the layout grid.
    // generate the grid based rowCount and noOfColumns and using existing wm-gridcolumn elements
    for (row = 0; row < rowCount; row++) {
      rowId = this.markupManagerService.getUniqueIdByType('gridrow');
      // Add rows until all existing columns are placed
      if (count < existingColumns.length) {
        htm += '<wm-gridrow widgetid=' + rowId + ' name=' + rowId + ' widgettype=' + "wm-gridrow" + '>';
      }
      for (col = 0; (col < noOfColumns && (count < existingNoOfFields)); col++) {
        existingCol = existingColumns.eq(count++);
        if (existingCol.find(formFieldElement).length > 0) {
          // If multiple rows and column should be hidden merge column within same row
          if (colDefs[colDefCount].show.toString() === 'false') {
            existingCol.attr('show', 'hide');
            col--;
          } else {
            existingCol.attr('show', 'show');
          }
          colDefCount++;
        } else {
          existingCol.attr('show', 'show');
        }
        if (existingCol.length) {
          existingCol.attr('columnwidth', columnWidth);
        }
        htm += existingCol ? existingCol[0].outerHTML : '';
      }
      htm += '</wm-gridrow>';
    }

    grid.append(htm);
  }

  /**
 * @ngdoc function
 * @name wmCore.$LiveWidgetsMarkupManager#updateMarkupForLiveForm
 * @methodOf wmCore.$LiveWidgetsMarkupManager
 * @function
 *
 * @description
 * Update the live form markup when the datasource is bound to a variable
 *
 * @param {options} options containing the form field and button properties
 */
  updateMarkupForLiveForm(options: any) {
    options.formFieldElement = 'wm-form-field';
    options.formActionElement = 'wm-form-action';

    let _markup = this.prepareFormFilterFieldsMarkup(options);
    runtimeEventManager.emit('compile-form-fields' + options.widgetId, {markup: _markup, props: options.properties});
    this.eventManager.emitUpdatePageStructure();
    return this.saveProject(options);
  }

  /**
 * @ngdoc function
 * @name wmCore.$LiveWidgetsMarkupManager#updateMarkupForLiveFilter
 * @methodOf wmCore.$LiveWidgetsMarkupManager
 * @function
 *
 * @description
 * Update the live filter markup when the datasource is bound to a variable
 *
 * @param {options} options containing the filter field and button properties
 */
  updateMarkupForLiveFilter(options: any) {
    options.formFieldElement = 'wm-filter-field';
    options.formActionElement = 'wm-filter-action';
    let _markup = this.prepareFormFilterFieldsMarkup(options);

    runtimeEventManager.emit('compile-form-fields' + options.widgetId, {
      markup: _markup,
      formDesigner: options.fromDesigner,
      variableName: options.variableName,
      props: options.properties
    });

    //TODO
    /* emit event to grid to compile the new columns and prepare fieldDefs */
    // $rs.$emit('compile-filters', options.scopeId, _markup, options.variableName, options.fromDesigner);

    this.eventManager.emitUpdatePageStructure();
    return this.saveProject(options);
  }
  /**
   * @ngdoc function
   * @name wmCore.$LiveWidgetsMarkupManager#updateMarkupForForm
   * @methodOf wmCore.$LiveWidgetsMarkupManager
   * @function
   *
   * @description
   * Update the form markup after saving advances settings
   *
   * @param {options} options containing the new properties
   */
  updateMarkupForForm(options: any) {
    options.formFieldElement = 'wm-form-field';
    options.formActionElement = 'wm-form-action';

    let _markup = this.prepareFormFilterFieldsMarkup(options);

    runtimeEventManager.emit('compile-form-fields' + options.widgetId, {markup: _markup, props: options.properties});
    this.eventManager.emitUpdatePageStructure();
    return this.saveProject(options);
  }

  updateFieldMarkup(options: any) {
    let gridColumn = this.markupManagerService.getMarkup()?.find('wm-liveform[name="' + options.formName + '"] wm-form-field[name="' + options.fieldName + '"]').parent();
    if (gridColumn?.is('wm-gridcolumn')) {
      gridColumn.removeAttr('show');
    }
  }
}
